import React, { useCallback, useState, createContext, useContext } from "react";
import { useMutation } from "@apollo/react-hooks";

import {
    setupUploadPhotos,
    setupUploadReleases,
    setupCancelRelease
} from "./helpers";
import {
    REGISTER_PHOTO_UPLOADS,
    REPORT_AWS_PHOTO_UPLOAD_RESULT,
    REGISTER_RELEASE_UPLOAD,
    SUBMIT_RELEASE
} from "./mutations";

export const PhotoUploadContext = createContext();

export const PhotoUploadProvider = ({ children }) => {
    const [currentPhotoShootId, setCurrentPhotoShootId] = useState(null);
    const [uploadRegistrationErrors, setUploadRegistrationErrors] = useState(
        []
    );
    const [duplicateFilenameErrors, setDuplicateFilenameErrors] = useState([]);
    const clearUploadRegistrationErrors = () => setUploadRegistrationErrors([]);
    const [registerPhotoUploads] = useMutation(REGISTER_PHOTO_UPLOADS);

    const [reportAwsPhotoUploadResult] = useMutation(
        REPORT_AWS_PHOTO_UPLOAD_RESULT
    );

    const handleRegisterUploadsResult = (errors = []) => {
        if (!errors.length) return;

        const duplicateFilenameErrors = errors.filter(
            error => error.error === "Duplicate file name. Rename to try again."
        );

        const otherErrors = errors.filter(
            error => error.error !== "Duplicate file name. Rename to try again."
        );

        setDuplicateFilenameErrors(duplicateFilenameErrors);
        setUploadRegistrationErrors(otherErrors);
    };

    const uploadPhotos = useCallback(
        setupUploadPhotos(
            registerPhotoUploads,
            reportAwsPhotoUploadResult,
            currentPhotoShootId,
            handleRegisterUploadsResult
        ),
        [registerPhotoUploads, reportAwsPhotoUploadResult, currentPhotoShootId]
    );

    const contextValues = {
        uploadPhotos,
        setCurrentPhotoShootId,
        currentPhotoShootId,
        uploadRegistrationErrors,
        clearUploadRegistrationErrors,
        duplicateFilenameErrors
    };

    return (
        <PhotoUploadContext.Provider value={contextValues}>
            {children}
        </PhotoUploadContext.Provider>
    );
};

export const usePhotoUpload = () => {
    const context = useContext(PhotoUploadContext);

    if (context === undefined) {
        throw new Error(
            "usePhotoUpload must be used within a PhotoUploadProvider"
        );
    }

    return context;
};

export const useReleaseUpload = () => {
    const [uploadRelease] = useMutation(REGISTER_RELEASE_UPLOAD);
    const [submitRelease] = useMutation(SUBMIT_RELEASE);

    const uploadReleases = useCallback(
        setupUploadReleases(uploadRelease, submitRelease),
        [uploadRelease, submitRelease]
    );
    const cancelRelease = useCallback(setupCancelRelease(submitRelease), [
        submitRelease
    ]);

    return {
        uploadReleases,
        cancelRelease
    };
};
